/**
 * Lazy-capture affordances on the bid overview (Specs/win-loss-harvest-spec.md,
 * widened in R4 plan §2.7):
 *  - "Submitted 3 Oct · Record result" while a submitted bid has no result.
 *  - "Add submission to library" while a submitted or closed bid has eligible
 *    but unharvested artifacts (also the retry surface after harvest failures).
 *  - "Record outcome details" while a won/lost bid lacks an outcome row.
 * Flat rows, quiet by design. They render nothing when there's nothing to do.
 */

import { useEffect } from 'react'
import { Library, Trophy, Loader2, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useHarvestStore } from '@/stores/harvest-store'
import { useBidsStore } from '@/stores/bids-store'
import { bidLifecycleOf, outcomeHasResult } from '@/lib/bid-lifecycle'
import { useBidReadOnly } from '@/hooks/useViewOnly'

const ROW_CLASS = 'group flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-2 hover:bg-muted/40'

/** "3 Oct", in the reader's locale (AU by default). */
function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

export function HarvestOutcomeAffordances({ bidId }: { bidId: string }) {
  const bid = useBidsStore((s) => (s.selectedBid?.id === bidId ? s.selectedBid : null))
  const status = useHarvestStore((s) => s.status)
  const statusBidId = useHarvestStore((s) => s.statusBidId)
  const isSaving = useHarvestStore((s) => s.isSaving)
  const loadStatus = useHarvestStore((s) => s.loadStatus)
  const harvestNow = useHarvestStore((s) => s.harvestNow)
  const openOutcomeDialog = useHarvestStore((s) => s.openOutcomeDialog)
  // Every row here is a write prompt (record a result, harvest, outcome details),
  // so none of it shows to an org viewer or a member who is not on the bid.
  const { isReadOnly } = useBidReadOnly(bidId)

  const position = bid ? bidLifecycleOf(bid) : null
  const isLodged = position?.lifecycle === 'lodged'
  const relevant = !isReadOnly && (isLodged || position?.lifecycle === 'closed')

  useEffect(() => {
    if (relevant) void loadStatus(bidId)
  }, [bidId, relevant, loadStatus])

  if (!bid || !position || !relevant) return null

  const harvest = statusBidId === bidId ? status : null
  const unharvested = harvest?.artifacts.filter((a) => a.eligible && !a.harvestedDocumentId) ?? []
  const showHarvest = harvest?.autoHarvestEnabled === true && unharvested.length > 0
  // A submitted bid always gets the prompt: it does not wait on harvest status.
  const showRecordResult = isLodged
  const showOutcomeDetails =
    harvest !== null && outcomeHasResult(position.outcome) && !harvest.outcome
  const lodgedOn = formatShortDate(bid.lodgedAt)

  if (!showHarvest && !showRecordResult && !showOutcomeDetails) return null

  return (
    <div className="space-y-1">
      {showRecordResult && (
        <div className={ROW_CLASS} data-testid="record-result-prompt">
          <Send className="h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="min-w-0 flex-1 truncate text-sm">
            {lodgedOn ? `Submitted ${lodgedOn}` : 'Submitted'}
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2.5 text-xs"
            onClick={() => openOutcomeDialog({ id: bidId, title: bid.title })}
          >
            Record result
          </Button>
        </div>
      )}
      {showHarvest && (
        <div className={ROW_CLASS}>
          <Library className="h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="min-w-0 flex-1 truncate text-sm">
            {unharvested.length} submitted document{unharvested.length === 1 ? '' : 's'} can be
            added to your library as past-bid content.
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 gap-1.5 px-2.5 text-xs"
            disabled={isSaving}
            onClick={() => void harvestNow(bidId, unharvested.map((a) => a.artifactId))}
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add submission to library
          </Button>
        </div>
      )}
      {showOutcomeDetails && (
        <div className={ROW_CLASS}>
          <Trophy className="h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="min-w-0 flex-1 truncate text-sm">
            This bid was {position.outcome === 'won' ? 'won' : 'lost'}. Capture the details while the debrief is fresh.
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 shrink-0 px-2.5 text-xs"
            onClick={() => openOutcomeDialog({ id: bidId, title: bid.title }, position.outcome)}
          >
            Record outcome details
          </Button>
        </div>
      )}
    </div>
  )
}
